// src/pages/Contact.js
import React, { useEffect, useRef } from 'react';
import { useContent } from '../context/ContentContext';
import ContactForm from '../components/contact/ContactForm';
import ContactInfo from '../components/contact/ContactInfo';
import { fadeIn } from '../utils/gsapAnimations';
import SectionLoader from '../components/common/SectionLoader';

function Contact() {
  const pageRef = useRef(null);
  const { content, loading, error } = useContent();

  useEffect(() => {
    fadeIn(pageRef.current, 0.3);
  }, []);

  if (loading) return <SectionLoader message="Loading contact page..." />;
  if (error) return (
    <div className="text-center py-16 text-xl font-montserrat text-red-600 bg-white rounded-2xl shadow-xl max-w-4xl mx-auto m-8">
      {error}
    </div>
  );

  const contactContent = content.contact || {};

  return (
    <div ref={pageRef} className="bg-cream min-h-screen py-12 lg:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero-like header card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 mb-12 lg:mb-16">
          <div className="p-8 lg:p-12 text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-playfair-display font-semibold text-primary mb-6 tracking-tight">
              {contactContent.title || 'Get in Touch'}
            </h1>
            <p className="text-lg sm:text-xl font-montserrat text-secondary max-w-3xl mx-auto leading-relaxed">
              We’d love to hear from you. Whether you have a question, feedback, or just want to say hello — reach out!
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Contact Form - main content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12 border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
              <h2 className="text-2xl lg:text-3xl font-playfair-display font-semibold text-primary mb-8">
                Send Us a Message
              </h2>
              <ContactForm />
            </div>
          </div>

          {/* Contact Info + Image Sidebar */}
          <div className="lg:col-span-2 space-y-8">
            {/* Info Card */}
            <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-10 border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
              <ContactInfo content={contactContent.info} />
            </div>

            {/* Image Card */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <img
                src={process.env.REACT_APP_STORE_URL + '/' + contactContent.image || 'https://via.placeholder.com/600x800?text=Contact+Us'}
                alt={contactContent.imageAlt || 'Contact Us'}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;